import { getSessionId } from "../hooks/useSession";
import type { Site, SiteSpecContent } from "@imonsite/shared";

// Production URLs use custom domains, dev uses localhost
const API_URL = import.meta.env.VITE_API_URL || 
  (import.meta.env.PROD ? "https://api.imonsite.co.uk" : "http://localhost:8787");
const AI_AGENT_URL = import.meta.env.VITE_AI_AGENT_URL || 
  (import.meta.env.PROD ? "https://agent.imonsite.co.uk" : "http://localhost:8788");
const BILLING_URL = import.meta.env.VITE_BILLING_URL || 
  (import.meta.env.PROD ? "https://billing.imonsite.co.uk" : "http://localhost:8791");

function getHeaders(): HeadersInit {
  return {
    "Content-Type": "application/json",
    "X-Session-ID": getSessionId(),
  };
}

export const api = {
  // Create a new site
  async createSite(initialMessage?: string): Promise<{ site: Site }> {
    const response = await fetch(`${API_URL}/sites`, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify({ initialMessage }),
    });
    if (!response.ok) {
      throw new Error(`Failed to create site: ${response.status}`);
    }
    return response.json();
  },

  // Get site by ID
  async getSite(siteId: string): Promise<{ site: Site }> {
    const response = await fetch(`${API_URL}/sites/${siteId}`, {
      headers: getHeaders(),
    });
    if (!response.ok) {
      throw new Error(`Failed to get site: ${response.status}`);
    }
    return response.json();
  },

  // Get current spec
  async getSpec(siteId: string): Promise<{
    spec: Partial<SiteSpecContent>;
    version: number;
  }> {
    const response = await fetch(`${API_URL}/sites/${siteId}/spec`, {
      headers: getHeaders(),
    });
    if (!response.ok) {
      throw new Error(`Failed to get spec: ${response.status}`);
    }
    return response.json();
  },

  // Get spec history
  async getSpecHistory(siteId: string): Promise<{
    specs: Array<{ id: string; version: number; createdAt: string }>;
  }> {
    const response = await fetch(`${API_URL}/sites/${siteId}/specs`, {
      headers: getHeaders(),
    });
    if (!response.ok) {
      throw new Error(`Failed to get spec history: ${response.status}`);
    }
    return response.json();
  },

  // Restore spec version
  async restoreSpec(
    siteId: string,
    version: number
  ): Promise<{ newVersion: number }> {
    const response = await fetch(
      `${API_URL}/sites/${siteId}/specs/${version}/restore`,
      {
        method: "POST",
        headers: getHeaders(),
      }
    );
    if (!response.ok) {
      throw new Error(`Failed to restore spec: ${response.status}`);
    }
    return response.json();
  },

  // Chat endpoint URL (for SSE)
  getChatUrl(siteId: string): string {
    return `${AI_AGENT_URL}/sites/${siteId}/chat`;
  },

  // Get chat history
  async getChatHistory(siteId: string): Promise<{
    messages: Array<{ id: string; siteId: string; role: string; content: string; createdAt: string }>;
  }> {
    const response = await fetch(`${AI_AGENT_URL}/sites/${siteId}/chat/history`, {
      headers: getHeaders(),
    });
    if (!response.ok) {
      throw new Error(`Failed to get chat history: ${response.status}`);
    }
    return response.json();
  },

  // Build site and get preview URL
  async buildSite(siteId: string): Promise<{ buildId: string; previewUrl: string }> {
    const response = await fetch(`${API_URL}/sites/${siteId}/build`, {
      method: "POST",
      headers: getHeaders(),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || `Build failed: ${response.status}`);
    }
    return response.json();
  },

  // Check subdomain availability
  async checkSubdomainAvailable(subdomain: string): Promise<{ available: boolean; reason?: string }> {
    const response = await fetch(`${API_URL}/subdomains/${encodeURIComponent(subdomain)}/available`, {
      headers: getHeaders(),
    });
    if (!response.ok) {
      throw new Error(`Failed to check subdomain: ${response.status}`);
    }
    return response.json();
  },

  // Reserve subdomain for a site
  async reserveSubdomain(siteId: string, subdomain: string): Promise<{ site: Site }> {
    const response = await fetch(`${API_URL}/sites/${siteId}`, {
      method: "PATCH",
      headers: getHeaders(),
      body: JSON.stringify({ subdomain }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || `Failed to reserve subdomain: ${response.status}`);
    }
    return response.json();
  },

  // Create checkout session for billing
  async createCheckout(siteId: string): Promise<{ checkoutUrl: string }> {
    const response = await fetch(`${BILLING_URL}/checkout`, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify({ siteId }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || `Failed to create checkout: ${response.status}`);
    }
    return response.json();
  },
};

